import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, Play, ChevronRight, Users, Trophy, Loader2, Tv } from "lucide-react";
import { Button } from "@/components/ui/button";
import { partyApi, getHost } from "@/lib/party";

const useCountdown = (endsAt) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!endsAt) return;
    const tick = () => setLeft(Math.max(0, Math.ceil((new Date(endsAt) - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [endsAt]);
  return left;
};

const Timer = ({ endsAt }) => {
  const left = useCountdown(endsAt);
  return <span className={`font-mono font-bold ${left <= 5 ? "text-neon-pink animate-pulse" : "text-neon-yellow"}`}>{left}s</span>;
};

export default function PartyHost() {
  const { code } = useParams();
  const navigate = useNavigate();
  const hostToken = getHost(code);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const dead = useRef(false);

  useEffect(() => {
    if (!hostToken) { navigate("/party"); return; }
    dead.current = false;
    let id;
    const poll = async () => {
      if (dead.current) return;
      try { setS(await partyApi.state(code, { hostToken })); } catch {}
    };
    poll();
    id = setInterval(poll, 1200);
    return () => { dead.current = true; clearInterval(id); };
  }, [code, hostToken, navigate]);

  const act = async (fn) => { setBusy(true); try { await fn(); } catch {} finally { setBusy(false); } };

  if (!s) return <Center><Loader2 className="w-8 h-8 animate-spin text-neon-cyan" /></Center>;

  const joinUrl = `${window.location.origin}/party?code=${code}`;
  const ph = s.phase || {};

  return (
    <div className="min-h-[80vh] max-w-6xl mx-auto px-4 md:px-8 py-8" data-testid="party-host">
      {/* top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div className="flex items-center gap-2">
          <Tv className="w-5 h-5 text-neon-cyan" />
          <span className="font-display font-bold uppercase tracking-widest text-sm">{s.mode === "trivia" ? "Trivia Brawl" : "Punchline Party"}</span>
          {s.status !== "lobby" && s.status !== "ended" && (
            <span className="text-zinc-500 font-mono text-sm">· Round {s.round}/{s.total_rounds}</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-zinc-400 text-sm"><Users className="w-4 h-4" /> {s.players.length}/{s.max_players}</div>
      </div>

      <AnimatePresence mode="wait">
        {/* LOBBY */}
        {s.status === "lobby" && (
          <motion.div key="lobby" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="grid md:grid-cols-2 gap-10 items-center">
            <div className="text-center md:text-left">
              <p className="text-zinc-400 uppercase tracking-widest text-sm font-bold mb-2">Join at <span className="text-white">{window.location.host}/party</span></p>
              <div className="font-display font-black text-7xl sm:text-8xl tracking-[0.15em] text-neon-cyan" data-testid="room-code"
                style={{ textShadow: "0 0 30px rgba(0,240,255,0.5)" }}>{code}</div>
              <div className="mt-6 inline-block bg-white p-3 rounded-xl">
                <QRCodeCanvas value={joinUrl} size={140} />
              </div>
            </div>
            <div>
              <h3 className="font-display font-bold uppercase tracking-widest text-sm text-zinc-400 mb-4">Players ({s.players.length})</h3>
              <div className="grid grid-cols-2 gap-3 min-h-[120px]" data-testid="lobby-players">
                {s.players.length === 0 && <p className="text-zinc-600 font-mono text-sm col-span-2">Waiting for players to join…</p>}
                {s.players.map((p) => (
                  <motion.div key={p.pid} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    className="flex items-center gap-2 bg-zinc-900 border-2 border-zinc-700 rounded-lg px-3 py-2">
                    <span className="text-2xl">{p.avatar}</span>
                    <span className="font-bold truncate">{p.name}</span>
                  </motion.div>
                ))}
              </div>
              <Button onClick={() => act(() => partyApi.start(code, hostToken))} disabled={busy || s.players.length < 1}
                data-testid="host-start-btn" className="mt-6 w-full bg-neon-pink text-white rounded-none font-bold uppercase tracking-widest h-14 text-lg hover:bg-white hover:text-black disabled:opacity-40">
                <Play className="w-5 h-5 mr-2 fill-current" /> Start Game
              </Button>
            </div>
          </motion.div>
        )}

        {/* TRIVIA QUESTION */}
        {s.status === "question" && (
          <motion.div key="q" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center" data-testid="host-question">
            <div className="flex justify-center mb-6"><Timer endsAt={ph.ends_at} /></div>
            <h2 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight max-w-3xl mx-auto leading-tight">{ph.q}</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-10 max-w-3xl mx-auto">
              {ph.options.map((o, i) => (
                <div key={i} className="rounded-xl border-2 border-zinc-700 bg-zinc-900 px-5 py-5 font-display font-bold text-xl text-left flex items-center gap-3">
                  <span className="w-8 h-8 rounded-md flex items-center justify-center text-black font-black" style={{ background: ["#00F0FF", "#FF003C", "#39FF14", "#EFFF04"][i] }}>{"ABCD"[i]}</span>
                  {o}
                </div>
              ))}
            </div>
            <p className="mt-8 text-zinc-400 font-mono">{ph.answered_count}/{s.players.length} answered</p>
            <HostNext busy={busy} onClick={() => act(() => partyApi.next(code, hostToken))} label="Skip to reveal" />
          </motion.div>
        )}

        {/* TRIVIA REVEAL */}
        {s.status === "reveal" && s.mode === "trivia" && (
          <motion.div key="rv" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="host-reveal">
            <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight max-w-3xl mx-auto">{ph.q}</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-8 max-w-3xl mx-auto">
              {ph.options.map((o, i) => (
                <div key={i} className={`rounded-xl border-2 px-5 py-4 font-display font-bold text-lg text-left flex items-center gap-3 ${i === ph.correct ? "border-neon-lime bg-neon-lime/10 text-neon-lime" : "border-zinc-800 bg-zinc-900 text-zinc-500"}`}>
                  <span className="w-7 h-7 rounded-md flex items-center justify-center text-black font-black" style={{ background: i === ph.correct ? "#39FF14" : "#3f3f46" }}>{"ABCD"[i]}</span>
                  {o}
                </div>
              ))}
            </div>
            <Scoreboard rows={ph.scoreboard} />
            <HostNext busy={busy} onClick={() => act(() => partyApi.next(code, hostToken))} label={ph.is_last ? "Final results" : "Next question"} />
          </motion.div>
        )}

        {/* PUNCHLINE ANSWERING */}
        {s.status === "answering" && (
          <motion.div key="ans" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center" data-testid="host-answering">
            <div className="flex justify-center mb-6"><Timer endsAt={ph.ends_at} /></div>
            <p className="text-neon-pink uppercase tracking-widest font-bold text-sm mb-3">Write your funniest answer on your phone</p>
            <h2 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight max-w-3xl mx-auto leading-tight">{ph.prompt}</h2>
            <p className="mt-10 text-zinc-400 font-mono">{ph.submitted_count}/{s.players.length} submitted</p>
            <HostNext busy={busy} onClick={() => act(() => partyApi.next(code, hostToken))} label="Lock answers" />
          </motion.div>
        )}

        {/* PUNCHLINE VOTING */}
        {s.status === "voting" && (
          <motion.div key="vote" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="host-voting">
            <div className="flex justify-center mb-4"><Timer endsAt={ph.ends_at} /></div>
            <p className="text-neon-cyan uppercase tracking-widest font-bold text-sm mb-2">Vote for the funniest!</p>
            <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight max-w-3xl mx-auto">{ph.prompt}</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-8 max-w-3xl mx-auto">
              {ph.answers.map((a, i) => (
                <div key={a.id} className="rounded-xl border-2 border-zinc-700 bg-zinc-900 px-5 py-5 font-display font-bold text-lg">{a.text}</div>
              ))}
            </div>
            <p className="mt-6 text-zinc-400 font-mono">{ph.vote_count} votes in</p>
            <HostNext busy={busy} onClick={() => act(() => partyApi.next(code, hostToken))} label="Reveal votes" />
          </motion.div>
        )}

        {/* PUNCHLINE REVEAL */}
        {s.status === "reveal" && s.mode === "punchline" && (
          <motion.div key="prv" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="host-reveal">
            <h2 className="font-display font-black text-2xl uppercase tracking-tight max-w-3xl mx-auto mb-6">{ph.prompt}</h2>
            <div className="space-y-3 max-w-2xl mx-auto">
              {ph.results.map((r, i) => (
                <div key={r.pid} className={`flex items-center gap-3 rounded-xl border-2 px-5 py-4 text-left ${i === 0 && r.votes > 0 ? "border-neon-yellow bg-neon-yellow/10" : "border-zinc-800 bg-zinc-900"}`}>
                  <span className="text-2xl">{r.avatar}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-lg leading-tight">{r.text}</p>
                    <p className="text-xs text-zinc-500 font-mono">{r.name}</p>
                  </div>
                  <span className="font-display font-black text-neon-lime">+{r.gained}</span>
                  <span className="text-zinc-400 font-mono text-sm">{r.votes}♥</span>
                </div>
              ))}
            </div>
            <Scoreboard rows={ph.scoreboard} />
            <HostNext busy={busy} onClick={() => act(() => partyApi.next(code, hostToken))} label={ph.is_last ? "Final results" : "Next round"} />
          </motion.div>
        )}

        {/* ENDED */}
        {s.status === "ended" && (
          <motion.div key="end" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center" data-testid="host-ended">
            <Crown className="w-12 h-12 text-neon-yellow mx-auto mb-3" />
            <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter">Winner</h2>
            {ph.final?.[0] && (
              <p className="font-display font-black text-3xl text-neon-cyan mt-3" data-testid="winner">{ph.final[0].avatar} {ph.final[0].name}</p>
            )}
            <div className="max-w-md mx-auto mt-8 space-y-2">
              {ph.final?.map((r, i) => (
                <div key={r.pid} className="flex items-center gap-3 bg-zinc-900 border-2 border-zinc-800 rounded-lg px-4 py-3">
                  <span className="font-display font-black text-zinc-500 w-6">{i + 1}</span>
                  <span className="text-2xl">{r.avatar}</span>
                  <span className="font-bold flex-1 text-left">{r.name}</span>
                  <span className="font-mono text-neon-lime">{r.score}</span>
                </div>
              ))}
            </div>
            <Button onClick={() => navigate("/party")} className="mt-8 bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest h-12 px-8 hover:bg-white">New Game</Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-[70vh] flex items-center justify-center">{children}</div>;

const HostNext = ({ onClick, label, busy }) => (
  <div className="mt-10">
    <Button onClick={onClick} disabled={busy} data-testid="host-next-btn"
      className="bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest h-12 px-8 hover:bg-white">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{label} <ChevronRight className="w-4 h-4 ml-1" /></>}
    </Button>
  </div>
);

const Scoreboard = ({ rows }) => (
  <div className="max-w-md mx-auto mt-10">
    <h3 className="flex items-center justify-center gap-2 font-display font-bold uppercase tracking-widest text-sm text-zinc-400 mb-3"><Trophy className="w-4 h-4 text-neon-yellow" /> Standings</h3>
    <div className="space-y-2">
      {rows?.map((r, i) => (
        <div key={r.pid} className="flex items-center gap-3 bg-zinc-900 border-2 border-zinc-800 rounded-lg px-4 py-2.5">
          <span className="font-display font-black text-zinc-500 w-5">{i + 1}</span>
          <span className="text-xl">{r.avatar}</span>
          <span className="font-bold flex-1 text-left truncate">{r.name}</span>
          <span className="font-mono text-neon-lime">{r.score}</span>
        </div>
      ))}
    </div>
  </div>
);
